import React from "react";
import { css, Theme, useTheme } from "@emotion/react";

import Image from "core/Components/Image";
import CommonButton from "core/Components/Buttons/CommonButton";
import mixins from "theme/mixins";

type Props = {
    data: BannerWithExtendedProfile,
    className?: string,
    height: number,
    buttonHeight: number,
    isUsingAltDesign: boolean,
};

const MainSection: React.FC<Props> = ({
    data,
    className = "",
    height,
    buttonHeight,
    isUsingAltDesign,
}) => {
    const theme: Theme = useTheme();

    const style = css`
        position: relative;
        display: grid;
        grid-template-columns: 50% 50%;
        grid-template-rows: 1fr;
        padding: 0 130px;
        height: ${height}px;
        overflow: hidden;
        background-color: ${data.backgroundColour};

        ${data.enableNoiseEffectBackground && css`
            ${mixins.noiseBackground}
        `}

        ${theme.breakpoints.down("lg")} {
            grid-template-columns: 1fr;
            justify-items: center;
            height: unset;
            padding: unset;
            overflow: visible;
        }
    `;

    const imageContainerStyle = css`
        grid-column: 1;
        grid-row: 1;
        align-self: stretch;
        position: relative;
        height: ${height}px;
        overflow: visible;

        > div {
            height: 100%;
        }

        img {
            height: 100% !important;
            width: auto !important;
            min-width: unset !important;
            min-height: unset !important;
        }

        ${theme.breakpoints.down("lg")} {
            grid-row: 1;
            overflow: hidden;
        }
    `;

    const contentContainerStyle = css`
        grid-column: 2;
        grid-row: 1;
        align-self: center;
        z-index: 1;

        h1 {
            font-size: 50px;
            font-weight: ${theme.fonts.weights.medium};
            color: ${data.headerColour};
            word-break: break-word;
            white-space: pre-line;
        }

        h2 {
            margin-top: 10px;
            font-size: 35px;
            font-weight: ${theme.fonts.weights.semiBold};
            color: ${data.subheaderColour};
        }

        h3 {
            margin-top: 30px;
            font-size: 25px;
            line-height: ${isUsingAltDesign ? "36px" : "40px"};
            white-space: pre-line;
            font-family: ${theme.fonts.openSans};
            font-weight: ${theme.fonts.weights.light};
            color: ${data.endorsementColour};
        }

        p {
            margin-top: 20px;
            color: ${data.endorserColour};
            font-weight: ${theme.fonts.weights.light};

            strong {
                font-weight: ${theme.fonts.weights.semiBold};
            }
        }

        ${theme.breakpoints.only("xxl")} {
            h1 {
                font-size: 45px;
            }

            h2 {
                font-size: 30px;
            }

            h3 {
                margin-top: 25px;
                font-size: 22px;
            }
        }

        ${theme.breakpoints.down("xl")} {
            h1 {
                font-size: 40px;
            }

            h2 {
                font-size: 28px;
            }

            h3 {
                margin-top: 20px;
                font-size: 18px;
            }
        }

        ${theme.breakpoints.down("lg")} {
            grid-column: 1;
            grid-row: 2;
            padding: 60px 100px 80px;
            width: 100%;
            background-color: ${data.backgroundColour};
        }

        ${theme.breakpoints.down("sm")} {
            padding-left: 60px;
            padding-right: 60px;
        }
        
        ${theme.breakpoints.down("xs")} {
            padding: 40px 30px 60px;
            
            h1 {
                font-size: 23.8px;
            }

            h2 {
                font-size: 16.66px;
            }
            
            h3 {
                font-size: 12px;
                line-height: 20px;
                white-space: unset;
            }

            p {
                font-size: 10px;
                line-height: 18px;
            }
        }
    `;

    const buttonStyle = css`
        margin-top: 60px;
        font-family: ${theme.fonts.figtree};

        ${theme.breakpoints.only("xxl")} {
            margin-top: 50px;
        }

        ${theme.breakpoints.down("xl")} {
            margin-top: 45px;
        }

        ${theme.breakpoints.down("lg")} {
            position: absolute;
            justify-self: center;
            margin: 0;
            bottom: -${buttonHeight / 2}px;
        }

        ${theme.breakpoints.down("sm")} {
            font-size: 16px;
        }
    `;

    return (
        <div css={style} className={className}>
            {/* Main Image */}
            <div css={imageContainerStyle}>
                <Image image={data.mainImage} useFocusPoint={data.useFocusPointMain} />
            </div>
            {/* Main Content */}
            <div css={contentContainerStyle}>
                <h1>{data.header}</h1>
                <h2>{data.subheader}</h2>
                <h3>{data.endorsement}</h3>
                {data.endorser && (
                    <div dangerouslySetInnerHTML={{ __html: data.endorser}} />
                )}
                <CommonButton
                    css={buttonStyle}
                    buttonData={{
                        outerURL: data.outerURL,
                        innerURL: data.innerURL,
                        buttonText: data.buttonText,
                        buttonColour: data.buttonColour,
                        buttonTextColour: data.buttonTextColour,
                        buttonHoverColour: data.buttonHoverColour,
                        buttonTextHoverColour: data.buttonTextHoverColour,
                    }}
                    isUsingAltDesign={isUsingAltDesign}
                />
            </div>
        </div>
    );
};

export default MainSection;
